import { FormLayout } from "@careinfo/layout";
import { tw } from "@careinfo/tailwind";
import { yupResolver } from "@hookform/resolvers/yup";
import { darken, lighten } from "polished";
import { useForm } from "react-hook-form";
import Button from "../../shared/Button";
import MultiImageField from "../../shared/MultiImageField";
import TextField from "../../shared/TextField";
import { createThemeValidation } from "../../utils/validationSchema";
import ThemeDisplay from "./ThemeDisplay";
import TextAreaField from "../../shared/TextAreaField";

const themes = [
  "primary",
  "secondary",
  "error",
  "warning",
  "success",
  "info",
  "backgroundDefault",
  "backgroundPaper",
  "textPrimary",
  "textSecondary",
  "textDisabled",
] as const;

const defaultThemeValues = {
  primary: null,
  secondary: null,
  error: null,
  warning: null,
  success: null,
  info: null,
  backgroundDefault: null,
  backgroundPaper: null,
  textPrimary: null,
  textSecondary: null,
  textDisabled: null,
};

interface Props {
  handleCreate: (value: any, setError: any) => void;
  isLoading: boolean;
  themeSetting: any;
}

const Theme = (props: Props) => {
  const { handleCreate, isLoading, themeSetting } = props;
  const {
    control,
    handleSubmit,
    setError,
    register,
    watch,
    getValues,
    formState: { errors },
    setValue,
  } = useForm<any>({
    defaultValues: async () => {
      return {
        ...themeSetting,
        color: themeSetting?.theme_settings?.primary,
        themeSetting: {
          ...defaultThemeValues,
          ...(themeSetting?.theme_settings ?? {}),
        },
      };
    },

    resolver: yupResolver(createThemeValidation),
  });

  const handleGenerate = () => {
    const color = getValues()?.["color"];
    if (color) {
      const shades = [];
      for (let i = 0; i < 5; i++) {
        shades.push({
          name: `${i + 1}00`,
          colorValue: lighten(`0.${i}`, color),
        });
      }

      for (let i = 0; i < 5; i++) {
        shades.push({
          name: `${i}00`,
          colorValue: darken(`0.${i}`, color),
        });
      }

      shades.push({
        name: "1100",
        colorValue: lighten(`0.445`, color),
      });

      shades.forEach((shade, i) => {
        let currentShade = shade?.colorValue;
        if (shade.colorValue.length === 4) {
          currentShade += currentShade.substring(1);
        }
        setValue("theme_settings." + themes[i], currentShade || null);
      });
    }
  };
  const theme_settings = watch("theme_settings");
  return (
    <>
      <FormLayout
        handleFormSubmit={handleCreate}
        handleSubmit={handleSubmit}
        setError={setError}
        buttonLabel="Save"
        isLoading={isLoading}
        disabled={false}
      >
        <>
          <div className={tw`grid grid-cols-3 gap-x-4 px-4`}>
            <div className={tw`w-full`}>
              <TextField
                control={control}
                name="title"
                placeholder="Enter a title"
                label="Title"
              />
            </div>
            <div className={tw`w-full`}>
              <TextField
                control={control}
                name="code"
                placeholder="Enter a code"
                label="Code"
              />
            </div>
            <div className={tw`flex justify-between items-end gap-x-2`}>
              <div className={tw`w-full`}>
                <label
                  htmlFor={register("color").name}
                  className={tw`flex flex-col cursor-pointer`}
                >
                  Color
                  <input
                    className={tw`w-full h-10 focus:outline-none `}
                    type="color"
                    {...register("color")}
                    id={register("color").name}
                  />
                </label>

                {errors?.color && (
                  <p className={tw`text-red-500 text-xs my-1`}>
                    {JSON.stringify(errors?.color?.message)}
                  </p>
                )}
              </div>

              <div className={tw`w-32 h-10`}>
                <Button
                  label="Generate"
                  type="button"
                  onClick={handleGenerate}
                />
              </div>
            </div>
            <MultiImageField name="logo" control={control} label="Logo" />
            <MultiImageField
              name="favicon_icon"
              control={control}
              label="Fav Icon"
            />
          </div>
          <div className={tw`px-4`}>
            <TextAreaField
              control={control}
              row={20}
              name="json_ld"
              placeholder="Write in json format"
              label="JSON-LD"
            />
            {themeSetting?.theme_settings || theme_settings ? (
              <>
                <div className={tw`grid grid-cols-4 gap-2 mt-6`}>
                  {themes.map((theme) => {
                    return (
                      <ThemeDisplay
                        key={crypto.randomUUID()}
                        control={control}
                        name={"theme_settings." + theme}
                        label={theme}
                      />
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        </>
      </FormLayout>
    </>
  );
};

export default Theme;
